import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { XYChartLegend } from './components/legend/XYChartLegend.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_LOADING_STATE,
} from './constants/defaults.js';
import { XYChartAxesConfigContext } from './context/axes/XYChartAxesConfig.context.js';
import { XYChartAnnotationsConfig } from './context/XYChartAnnotationsConfig.context.js';
import { XYChartColorPaletteContext } from './context/XYChartColorPalette.context.js';
import { XYChartThresholdsContext } from './context/XYChartThresholds.context.js';
import { XYChartToolbarContext } from './context/XYChartToolbar.context.js';
import { XYChartTruncationModeContext } from './context/XYChartTruncationMode.context.js';
import { useXYChartConfig } from './hooks/useXYChartConfig.js';
import { XYChartAnnotationsDataProvider } from './providers/XYChartAnnotationsDataProvider.js';
import { XYChartAnnotationsShownIndicatorsProvider } from './providers/XYChartAnnotationsShownIndicatorsProvider.js';
import { XYChartColorScalesProvider } from './providers/XYChartColorScalesProvider.js';
import { XYChartChartDataProvider } from './providers/XYChartDataProvider.js';
import { XYChartDownloadProvider } from './providers/XYChartDownloadProvider.js';
import { XYChartIdProvider } from './providers/XYChartIdProvider.js';
import { XYChartStoreDomainSync } from './providers/XYChartStoreDomainSync.js';
import { XYChartStoreProvider } from './providers/XYChartStoreProvider.js';
import { XYChartVisibleAxesProvider } from './providers/XYChartVisibleAxesProvider.js';
import { Annotations } from './slots/Annotations/Annotations.js';
import { Marker } from './slots/Annotations/Marker.js';
import { AnnotationsTooltip } from './slots/Annotations/Tooltip.js';
import { Track } from './slots/Annotations/Track.js';
import { AreaSeries } from './slots/AreaSeries.js';
import { BarSeries } from './slots/BarSeries.js';
// DEPRECATION: [APPDEV-17650] BREAKING-CHANGE: Remove CustomTooltip slot.
import { CustomTooltip } from './slots/CustomTooltip.js';
import { DotSeries } from './slots/DotSeries.js';
import { Legend } from './slots/Legend.js';
import { LineSeries } from './slots/LineSeries.js';
import { RectSeries } from './slots/RectSeries.js';
import { Select } from './slots/Select.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { Toolbar } from './slots/Toolbar.js';
import { XYChartTooltipSlot as Tooltip } from './slots/Tooltip.js';
import { XAxis } from './slots/XAxis.js';
import { YAxis } from './slots/YAxis.js';
import type { XYChartProps } from './types/xy-chart-props.js';
import { XYChartRef } from './types/xy-chart.js';
import { isEmptyData } from './utils/is-empty-data.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { buildInternalConfig } from './utils/xy-chart-configuration.js';
import { XYChart as XYChartInternal } from './XYChart.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { OverlayProvider } from '../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ColorRuleContext } from '../core/context/ColorRuleContext.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

/**
 * Component name used for data-dt-component in behavioural tracking
 */
const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'XYChart';

/**
 * @internal
 */
export const XYChartRoot = /* @__PURE__ */ forwardRef<
  XYChartRef,
  PropsWithChildren<XYChartProps>
>((props, ref) => {
  const {
    data,
    children,
    height,
    width,
    loading = DEFAULT_LOADING_STATE,
    style,
    className,
    __downloadDataCallback: downloadDataCallback,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);
  const slots = iterateSlots(props.children);
  const config = useXYChartConfig(props, slots);
  const { emptyState, errorState } = getChartStatesTemplates(props.children);
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<XYChartRef | HTMLDivElement>([
    ref,
    containerRef,
  ]);

  const {
    hidden: legendHidden,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    onRatioChange,
    position: legendPosition,
  } = config.legend;

  const legendRatio = initialRatio ?? ratio;

  const chartHeight = coerceSizeValue(height ?? DEFAULT_CHART_HEIGHT);

  const internalConfig = buildInternalConfig(config, slots);
  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <XYChartIdProvider>
      <XYChartThresholdsContext.Provider value={internalConfig.thresholds}>
        <XYChartAxesConfigContext.Provider value={internalConfig.axes}>
          <XYChartColorPaletteContext.Provider value={config.colorPalette}>
            <XYChartDownloadProvider
              downloadDataCallback={downloadDataCallback}
            >
              <XYChartToolbarContext.Provider value={config.toolbar}>
                <XYChartTruncationModeContext.Provider
                  value={{ truncationMode: config.truncationMode }}
                >
                  <XYChartAnnotationsConfig.Provider value={config.annotations}>
                    <ColorRuleContext.Provider value={config.colorRules}>
                      <XYChartStoreProvider>
                        <XYChartChartDataProvider
                          data={data}
                          seriesVariantsProps={internalConfig.series}
                        >
                          <XYChartStoreDomainSync>
                            <XYChartAnnotationsDataProvider>
                              <XYChartVisibleAxesProvider>
                                <XYChartColorScalesProvider>
                                  <XYChartAnnotationsShownIndicatorsProvider>
                                    <OverlayProvider>
                                      <TooltipProvider>
                                        <ChartLayout
                                          ref={chartLayoutRef}
                                          chartHeight={chartHeight}
                                          chartWidth={width}
                                          className={className}
                                          emptyState={emptyState}
                                          errorState={errorState}
                                          isEmpty={isEmptyData(children, data)}
                                          showLoader={loading}
                                          style={style}
                                          data-dtrum-mask={dataDtrumMask}
                                          data-dtrum-allow={dataDtrumAllow}
                                          {...behaviorTrackingProps}
                                          data-dt-component={
                                            COMPONENT_NAME_BEHAVIOURAL_TRACKING
                                          }
                                        >
                                          <ChartLayout.Graph>
                                            <XYChartInternal
                                              config={config}
                                              ref={rootRef}
                                            />
                                          </ChartLayout.Graph>
                                          {!legendHidden && (
                                            <ChartLayout.Legend
                                              position={legendPosition}
                                              initialRatio={legendRatio}
                                              onResize={onRatioChange}
                                            >
                                              <XYChartLegend />
                                            </ChartLayout.Legend>
                                          )}
                                        </ChartLayout>
                                      </TooltipProvider>
                                    </OverlayProvider>
                                  </XYChartAnnotationsShownIndicatorsProvider>
                                </XYChartColorScalesProvider>
                              </XYChartVisibleAxesProvider>
                            </XYChartAnnotationsDataProvider>
                          </XYChartStoreDomainSync>
                        </XYChartChartDataProvider>
                      </XYChartStoreProvider>
                    </ColorRuleContext.Provider>
                  </XYChartAnnotationsConfig.Provider>
                </XYChartTruncationModeContext.Provider>
              </XYChartToolbarContext.Provider>
            </XYChartDownloadProvider>
          </XYChartColorPaletteContext.Provider>
        </XYChartAxesConfigContext.Provider>
      </XYChartThresholdsContext.Provider>
    </XYChartIdProvider>
  );
});

/**
 * The `XYChart` encompasses various types of charts that utilize both x-axis and
 * y-axis for data representation, designed to visually display and analyze data
 * that involve two numerical variables.
 * @public
 */
export const XYChart = Object.assign(XYChartRoot, {
  Annotations,
  RectSeries,
  DotSeries,
  LineSeries,
  BarSeries,
  AreaSeries,
  XAxis,
  YAxis,
  Legend,
  Toolbar,
  /** @deprecated Use `XYChart.Tooltip` instead. */
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  CustomTooltip,
  Tooltip,
  Intent,
  Select,
  ThresholdIndicator,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  ColorRule,
});

(XYChart as typeof XYChart & { displayName: string }).displayName = 'XYChart';

/**
 * XYAnnotations Slots
 * @public
 */
export const XYChartAnnotations = {
  Marker,
  Track,
  Tooltip: AnnotationsTooltip,
};
